/**
 * Wallet Pass Service
 * Issues and serves Apple Wallet and Google Wallet loyalty passes for Convercus programs. Internal administrators register programs, mint program API keys, maintain store locations, configure wallet design and levels, read adoption analytics and query the audit trail; customer-facing clients download a pass through the program-scoped download routes, and the wallets themselves keep it current through the Apple PassKit web service and the Google Wallet Objects callback. Every path below already carries the gateway base prefix `/wallet-pass-service`.
 *
 * The version of the OpenAPI document: 1.0.39938
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */

import { Inject, Injectable, Optional }                      from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams,
         HttpResponse, HttpEvent, HttpParameterCodec }       from '@angular/common/http';
import { CustomHttpParameterCodec }                          from '../encoder';
import { Observable }                                        from 'rxjs';

import { ApiKeyCreatedDto } from '../model/models';
import { ApiKeyListItemDto } from '../model/models';
import { CreateApiKeyDto } from '../model/models';
import { InlineResponse401 } from '../model/models';

import { BASE_PATH, COLLECTION_FORMATS }                     from '../variables';
import { Configuration }                                     from '../configuration';
import {
    APIKeysServiceInterface,
    CreateApiKeyRequestParams,
    CreateApiKeyLegacyRequestParams,
    DeleteApiKeyRequestParams,
    DeleteApiKeyLegacyRequestParams,
    ListApiKeysRequestParams,
    ListApiKeysLegacyRequestParams
} from './api-keys.serviceInterface';



@Injectable({
  providedIn: 'root'
})
export class APIKeysService implements APIKeysServiceInterface {

    protected basePath = 'https://staging.convercus.io';
    public defaultHeaders = new HttpHeaders();
    public configuration = new Configuration();
    public encoder: HttpParameterCodec;

    constructor(protected httpClient: HttpClient, @Optional()@Inject(BASE_PATH) basePath: string, @Optional() configuration: Configuration) {
        if (configuration) {
            this.configuration = configuration;
        }
        if (typeof this.configuration.basePath !== 'string') {
            if (typeof basePath !== 'string') {
                basePath = this.basePath;
            }
            this.configuration.basePath = basePath;
        }
        this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }


    private addToHttpParams(httpParams: HttpParams, value: any, key?: string): HttpParams {
        if (typeof value === "object" && value instanceof Date === false) {
            httpParams = this.addToHttpParamsRecursive(httpParams, value);
        } else {
            httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
        }
        return httpParams;
    }

    private addToHttpParamsRecursive(httpParams: HttpParams, value?: any, key?: string): HttpParams {
        if (value == null) {
            return httpParams;
        }

        if (typeof value === "object") {
            if (Array.isArray(value)) {
                (value as any[]).forEach( elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
            } else if (value instanceof Date) {
                if (key != null) {
                    httpParams = httpParams.append(key,
                        (value as Date).toISOString().substr(0, 10));
                } else {
                   throw Error("key may not be null if value is Date");
                }
            } else {
                Object.keys(value).forEach( k => httpParams = this.addToHttpParamsRecursive(
                    httpParams, value[k], key != null ? `${key}.${k}` : k));
            }
        } else if (key != null) {
            httpParams = httpParams.append(key, value);
        } else {
            throw Error("key may not be null if value is not object or array");
        }
        return httpParams;
    }

    /**
     * Create program API key
     * Mints a new secret for the program and returns it in full — this is the only place the value is generated. Type &#x60;Program&#x60; authorises pass download; type &#x60;Internal&#x60; authorises admin routes for that program. &#x60;SuperInternal&#x60; is rejected with 400.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public createApiKey(requestParameters: CreateApiKeyRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<ApiKeyCreatedDto>;
    public createApiKey(requestParameters: CreateApiKeyRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<ApiKeyCreatedDto>>;
    public createApiKey(requestParameters: CreateApiKeyRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<ApiKeyCreatedDto>>;
    public createApiKey(requestParameters: CreateApiKeyRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const createApiKeyDto = requestParameters.createApiKeyDto;
        if (createApiKeyDto === null || createApiKeyDto === undefined) {
            throw new Error('Required parameter createApiKeyDto was null or undefined when calling createApiKey.');
        }
        const programId = requestParameters.programId;
        if (programId === null || programId === undefined) {
            throw new Error('Required parameter programId was null or undefined when calling createApiKey.');
        }
        const xAdminKey = requestParameters.xAdminKey;

        let headers = this.defaultHeaders;
        if (xAdminKey !== undefined && xAdminKey !== null) {
            headers = headers.set('x-admin-key', String(xAdminKey));
        }

        let credential: string | undefined;
        // authentication (Admin) required
        credential = this.configuration.lookupCredential('Admin');
        if (credential) {
            headers = headers.set('x-admin-key', credential);
        }

        // authentication (Admin JWT) required
        credential = this.configuration.lookupCredential('Admin JWT');
        if (credential) {
            headers = headers.set('Authorization', 'Bearer ' + credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.post<ApiKeyCreatedDto>(`${this.configuration.basePath}/wallet-pass-service/v1/admin/programs/${encodeURIComponent(String(programId))}/api-keys`,
            createApiKeyDto,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Create API key legacy
     * Superseded by POST /v1/admin/programs/{programId}/api-keys. Mints a new secret for the program and returns it in full — this is the only place the value is generated. Type &#x60;Program&#x60; authorises pass download, type &#x60;Internal&#x60; authorises the admin routes for that one program; &#x60;SuperInternal&#x60; is rejected with 400.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     * @deprecated
     */
    public createApiKeyLegacy(requestParameters: CreateApiKeyLegacyRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<ApiKeyCreatedDto>;
    public createApiKeyLegacy(requestParameters: CreateApiKeyLegacyRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<ApiKeyCreatedDto>>;
    public createApiKeyLegacy(requestParameters: CreateApiKeyLegacyRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<ApiKeyCreatedDto>>;
    public createApiKeyLegacy(requestParameters: CreateApiKeyLegacyRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const createApiKeyDto = requestParameters.createApiKeyDto;
        if (createApiKeyDto === null || createApiKeyDto === undefined) {
            throw new Error('Required parameter createApiKeyDto was null or undefined when calling createApiKeyLegacy.');
        }
        const programId = requestParameters.programId;
        if (programId === null || programId === undefined) {
            throw new Error('Required parameter programId was null or undefined when calling createApiKeyLegacy.');
        }
        const xAdminKey = requestParameters.xAdminKey;

        let headers = this.defaultHeaders;
        if (xAdminKey !== undefined && xAdminKey !== null) {
            headers = headers.set('x-admin-key', String(xAdminKey));
        }

        let credential: string | undefined;
        // authentication (Admin) required
        credential = this.configuration.lookupCredential('Admin');
        if (credential) {
            headers = headers.set('x-admin-key', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.post<ApiKeyCreatedDto>(`${this.configuration.basePath}/wallet-pass-service/admin/programs/${encodeURIComponent(String(programId))}/api-keys`,
            createApiKeyDto,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Delete program API key
     * Removes the key; subsequent calls authenticated with it fail.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public deleteApiKey(requestParameters: DeleteApiKeyRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<any>;
    public deleteApiKey(requestParameters: DeleteApiKeyRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<any>>;
    public deleteApiKey(requestParameters: DeleteApiKeyRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<any>>;
    public deleteApiKey(requestParameters: DeleteApiKeyRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const programId = requestParameters.programId;
        if (programId === null || programId === undefined) {
            throw new Error('Required parameter programId was null or undefined when calling deleteApiKey.');
        }
        const apiKeyId = requestParameters.apiKeyId;
        if (apiKeyId === null || apiKeyId === undefined) {
            throw new Error('Required parameter apiKeyId was null or undefined when calling deleteApiKey.');
        }
        const xAdminKey = requestParameters.xAdminKey;

        let headers = this.defaultHeaders;
        if (xAdminKey !== undefined && xAdminKey !== null) {
            headers = headers.set('x-admin-key', String(xAdminKey));
        }

        let credential: string | undefined;
        // authentication (Admin) required
        credential = this.configuration.lookupCredential('Admin');
        if (credential) {
            headers = headers.set('x-admin-key', credential);
        }

        // authentication (Admin JWT) required
        credential = this.configuration.lookupCredential('Admin JWT');
        if (credential) {
            headers = headers.set('Authorization', 'Bearer ' + credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.delete<any>(`${this.configuration.basePath}/wallet-pass-service/v1/admin/programs/${encodeURIComponent(String(programId))}/api-keys/${encodeURIComponent(String(apiKeyId))}`,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Delete API key legacy
     * Superseded by DELETE /v1/admin/programs/{programId}/api-keys/{apiKeyId}. Removes the key; subsequent calls authenticated with it fail.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     * @deprecated
     */
    public deleteApiKeyLegacy(requestParameters: DeleteApiKeyLegacyRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<any>;
    public deleteApiKeyLegacy(requestParameters: DeleteApiKeyLegacyRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<any>>;
    public deleteApiKeyLegacy(requestParameters: DeleteApiKeyLegacyRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<any>>;
    public deleteApiKeyLegacy(requestParameters: DeleteApiKeyLegacyRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const programId = requestParameters.programId;
        if (programId === null || programId === undefined) {
            throw new Error('Required parameter programId was null or undefined when calling deleteApiKeyLegacy.');
        }
        const apiKeyId = requestParameters.apiKeyId;
        if (apiKeyId === null || apiKeyId === undefined) {
            throw new Error('Required parameter apiKeyId was null or undefined when calling deleteApiKeyLegacy.');
        }
        const xAdminKey = requestParameters.xAdminKey;

        let headers = this.defaultHeaders;
        if (xAdminKey !== undefined && xAdminKey !== null) {
            headers = headers.set('x-admin-key', String(xAdminKey));
        }

        let credential: string | undefined;
        // authentication (Admin) required
        credential = this.configuration.lookupCredential('Admin');
        if (credential) {
            headers = headers.set('x-admin-key', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.delete<any>(`${this.configuration.basePath}/wallet-pass-service/admin/programs/${encodeURIComponent(String(programId))}/api-keys/${encodeURIComponent(String(apiKeyId))}`,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * List program API keys
     * Returns every key issued for the program, newest first. List items redact the secret value.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public listApiKeys(requestParameters: ListApiKeysRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<Array<ApiKeyListItemDto>>;
    public listApiKeys(requestParameters: ListApiKeysRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<Array<ApiKeyListItemDto>>>;
    public listApiKeys(requestParameters: ListApiKeysRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<Array<ApiKeyListItemDto>>>;
    public listApiKeys(requestParameters: ListApiKeysRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const programId = requestParameters.programId;
        if (programId === null || programId === undefined) {
            throw new Error('Required parameter programId was null or undefined when calling listApiKeys.');
        }
        const xAdminKey = requestParameters.xAdminKey;

        let headers = this.defaultHeaders;
        if (xAdminKey !== undefined && xAdminKey !== null) {
            headers = headers.set('x-admin-key', String(xAdminKey));
        }

        let credential: string | undefined;
        // authentication (Admin) required
        credential = this.configuration.lookupCredential('Admin');
        if (credential) {
            headers = headers.set('x-admin-key', credential);
        }

        // authentication (Admin JWT) required
        credential = this.configuration.lookupCredential('Admin JWT');
        if (credential) {
            headers = headers.set('Authorization', 'Bearer ' + credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.get<Array<ApiKeyListItemDto>>(`${this.configuration.basePath}/wallet-pass-service/v1/admin/programs/${encodeURIComponent(String(programId))}/api-keys`,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * List API keys legacy
     * Superseded by GET /v1/admin/programs/{programId}/api-keys. Returns every key issued for the program, newest first.
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     * @deprecated
     */
    public listApiKeysLegacy(requestParameters: ListApiKeysLegacyRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<Array<ApiKeyListItemDto>>;
    public listApiKeysLegacy(requestParameters: ListApiKeysLegacyRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpResponse<Array<ApiKeyListItemDto>>>;
    public listApiKeysLegacy(requestParameters: ListApiKeysLegacyRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json'}): Observable<HttpEvent<Array<ApiKeyListItemDto>>>;
    public listApiKeysLegacy(requestParameters: ListApiKeysLegacyRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json'}): Observable<any> {
        const programId = requestParameters.programId;
        if (programId === null || programId === undefined) {
            throw new Error('Required parameter programId was null or undefined when calling listApiKeysLegacy.');
        }
        const xAdminKey = requestParameters.xAdminKey;

        let headers = this.defaultHeaders;
        if (xAdminKey !== undefined && xAdminKey !== null) {
            headers = headers.set('x-admin-key', String(xAdminKey));
        }

        let credential: string | undefined;
        // authentication (Admin) required
        credential = this.configuration.lookupCredential('Admin');
        if (credential) {
            headers = headers.set('x-admin-key', credential);
        }

        let httpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }


        let responseType_: 'text' | 'json' = 'json';
        if(httpHeaderAcceptSelected && httpHeaderAcceptSelected.startsWith('text')) {
            responseType_ = 'text';
        }

        return this.httpClient.get<Array<ApiKeyListItemDto>>(`${this.configuration.basePath}/wallet-pass-service/admin/programs/${encodeURIComponent(String(programId))}/api-keys`,
            {
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

}
